import { Controller } from '@hotwired/stimulus';
import { UnsavedChangesGuard } from '../lib/unsaved_changes_guard.js';

export default class extends Controller {
  static targets = ['status', 'preview'];

  connect() {
    this.forms = new Map(Array.from(this.element.querySelectorAll('form'), (form) => [form, this.snapshot(form)]));
    this.pending = new Map();
    this.messageValue = 'Discard your unsaved character changes?';
    this.guard = new UnsavedChangesGuard(this, false);
    this.listeners = [];
    this.listen(document, 'click', (event) => {
      const link = event.target.closest('a[href]');
      if (link && !link.matches('[target="_blank"], [href^="#"]') && !event.ctrlKey && !event.metaKey)
        this.navigate(event);
    });
    this.listen(document, 'turbo:before-visit', this.navigate);
    this.listen(window, 'beforeunload', (event) => this.guard.confirmUnload(event));
    this.listen(window, 'popstate', (event) => this.guard.confirmHistory(event));
  }

  disconnect() {
    this.listeners.forEach(([target, name, listener]) => target.removeEventListener(name, listener, true));
    this.guard.disconnect();
  }

  listen(target, name, method) {
    const listener = method.bind(this);
    target.addEventListener(name, listener, true);
    this.listeners.push([target, name, listener]);
  }

  snapshot(form) {
    return JSON.stringify(Array.from(new FormData(form)).filter(([name]) => name !== 'authenticity_token'));
  }

  changed() {
    this.guard.update([...this.forms].some(([form, baseline]) => this.snapshot(form) !== baseline));
  }

  started(event) {
    this.pending.set(event.target, this.snapshot(event.target));
    this.statusTarget.textContent = 'Saving…';
  }

  finished(event) {
    const form = event.target;
    if ([403, 404].includes(event.detail.fetchResponse?.statusCode)) {
      this.pending.delete(form);
      this.dispatch('access-lost');
      return;
    }
    if (event.detail.success) {
      this.forms.set(form, this.pending.get(form));
      const description = form.querySelector('[name="character[description]"]');
      if (description && this.hasPreviewTarget)
        this.previewTarget.textContent = description.value || 'No description provided.';
      this.statusTarget.textContent = 'Saved';
    } else {
      this.statusTarget.textContent = 'Could not save. Your changes are still here; try again before switching.';
    }
    this.pending.delete(form);
    this.changed();
  }

  navigate(event) {
    if (this.pending.size) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.statusTarget.textContent = 'Saving changes. Please wait before switching.';
      return;
    }
    this.guard.confirmVisit(event);
    if (event.defaultPrevented) event.stopImmediatePropagation();
  }

  preserve(event) {
    if (this.guard.dirty || this.pending.size) event.preventDefault();
  }

  morphed(event) {
    if (event.target !== this.element || this.guard.dirty || this.pending.size) return;
    this.forms = new Map(Array.from(this.element.querySelectorAll('form'), (form) => [form, this.snapshot(form)]));
  }
}
